import { useState } from 'react';
import clsx from 'clsx';
import {
  Panel,
  Skeleton,
  EmptyState,
  Button,
  StatusBadge,
  ConfirmModal,
} from '@/components/ui';
import {
  useAdminModels,
  useDeleteModel,
  useUpdateSignalVariant,
  useUpdateModelEnabled,
  useUpdateModelName,
} from '@/hooks/use-models-v2';
import { Upload, Trash2, Pencil, Check, X } from 'lucide-react';
import type { SignalVariant } from '@arcanum-hq/shared-types';

// builder-ux-overhaul Phase 5 Session 3 (2026-05-22)
// Static Record avoids bg-${state} interpolation (Tailwind v4 has no safelist
// — silent class elimination). web CLAUDE.md F03 / § 11 R6.
const ENABLE_BTN_CLASS: Record<'active' | 'inactive', string> = {
  active:
    'px-2 py-1 text-xs font-mono text-warn bg-transparent border border-warn/20 cursor-pointer rounded-[2px] hover:bg-warn/[0.08] transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
  inactive:
    'px-2 py-1 text-xs font-mono text-accent bg-transparent border border-accent/20 cursor-pointer rounded-[2px] hover:bg-accent-dim transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
};

interface ModelListProps {
  onSelectModel: (id: string) => void;
  selectedModel: string | null;
  onConfigure?: (id: string) => void;
}

export function ModelList({
  onSelectModel,
  selectedModel,
  onConfigure,
}: ModelListProps) {
  // builder-ux-overhaul Phase 5 F02 fix (2026-05-22) — use admin-all endpoint
  // so disabled model families appear in this table and the Enable button is
  // reachable. New-deal dropdown (NewConversationModal) stays on useModels()
  // (active-only — must NOT show disabled models).
  const { data, isLoading, isError, error, refetch } = useAdminModels();

  const deleteModel = useDeleteModel();
  // agent-ux-optimization Phase 6 Session 2 (2026-05-08) — admin assigns the
  // Signal tab variant per model. Empty-string sentinel represents `null`
  // (unassigned) on the wire so the native <select> stays controlled.
  const updateSignalVariant = useUpdateSignalVariant();
  // builder-ux-overhaul Phase 5 Session 3 (2026-05-22) — admin enable/disable.
  const updateModelEnabled = useUpdateModelEnabled();
  // 2026-05-27 — admin rename. `editingId` tracks which row is in edit mode;
  // `draftName` is the controlled input value. Reset on save/cancel/escape.
  const updateModelName = useUpdateModelName();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');
  // ConfirmModal state for the destructive Disable action (re-enable needs no
  // confirm). pendingDisableId tracks which model row triggered the confirm.
  const [pendingDisableId, setPendingDisableId] = useState<string | null>(null);

  const startEdit = (id: string, currentName: string): void => {
    setEditingId(id);
    setDraftName(currentName);
  };
  const cancelEdit = (): void => {
    setEditingId(null);
    setDraftName('');
  };
  const commitEdit = (id: string, originalName: string): void => {
    const next = draftName.trim();
    if (!next || next === originalName) {
      cancelEdit();
      return;
    }
    updateModelName.mutate({ id, name: next }, { onSettled: cancelEdit });
  };

  return (
    <Panel title="Models">
      {isLoading && (
        <div className="flex flex-col gap-2" data-testid="model-list-loading">
          <Skeleton className="w-full" />
          <Skeleton className="w-4/5" />
          <Skeleton className="w-3/5" />
        </div>
      )}

      {isError && (
        <EmptyState
          title="FAILED TO LOAD MODELS"
          description={
            error instanceof Error
              ? error.message
              : 'An unexpected error occurred'
          }
          action={
            <Button
              variant="secondary"
              onClick={() => refetch()}
              data-testid="retry-button"
            >
              Retry
            </Button>
          }
        />
      )}

      {!isLoading && !isError && (!data?.data || data.data.length === 0) && (
        <EmptyState
          icon={<Upload size={32} />}
          title="NO MODELS UPLOADED YET"
          description="Upload an Excel model to get started with AI underwriting"
          action={<Button variant="primary">Upload Model</Button>}
        />
      )}

      {!isLoading && !isError && data?.data && data.data.length > 0 && (
        // 2026-05-27 — wrap in overflow-x-auto so the rightmost columns
        // (Enable/Disable + Configure/Delete) stay reachable on narrow viewport
        // widths. Pre-fix, the Configure button silently clipped off the
        // right edge of the Panel and users couldn't click it.
        <div className="overflow-x-auto" data-testid="model-list-scroll">
          <table
            className="w-full border-collapse min-w-[820px]"
            data-testid="model-list-table"
          >
            <thead>
              <tr>
                {[
                  'Name',
                  'Version',
                  'Inputs',
                  'Outputs',
                  'Signal Variant',
                  'Status',
                  'Enable/Disable',
                  '',
                ].map((h) => (
                  <th
                    key={h}
                    className="text-left px-2 py-1.5 text-xs text-text-dim font-mono font-medium border-b border-panel-border uppercase tracking-[0.6px]"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.data.map((model) => (
                <tr
                  key={model.id}
                  onClick={() => onSelectModel(model.id)}
                  className={clsx(
                    'cursor-pointer transition-colors duration-200',
                    selectedModel === model.id
                      ? 'bg-accent-faint'
                      : 'hover:bg-accent-faint/50'
                  )}
                  data-testid="model-row"
                >
                  {/* 2026-05-27 — inline rename. Click pencil → controlled input
                    + save/cancel. Enter commits, Escape cancels, blur on the
                    input commits (not cancels) to match the "save by default"
                    intent. Disabled while the mutation is pending. */}
                  <td className="px-2 py-2 text-sm font-mono text-text-bright font-medium">
                    {editingId === model.id ? (
                      <div
                        className="flex items-center gap-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="text"
                          value={draftName}
                          autoFocus
                          maxLength={100}
                          disabled={updateModelName.isPending}
                          onChange={(e) => setDraftName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              commitEdit(model.id, model.name);
                            } else if (e.key === 'Escape') {
                              e.preventDefault();
                              cancelEdit();
                            }
                          }}
                          data-testid={`model-name-input-${model.id}`}
                          aria-label={`Rename ${model.name}`}
                          className="px-2 py-1 text-sm font-mono text-text-bright bg-input-bg border border-input-border rounded-[2px] outline-none focus:border-accent disabled:opacity-50 min-w-[200px]"
                        />
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            commitEdit(model.id, model.name);
                          }}
                          disabled={updateModelName.isPending}
                          title="Save"
                          data-testid={`model-name-save-${model.id}`}
                          className="p-1 text-accent hover:bg-accent-dim border border-accent/20 rounded-[2px] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <Check size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            cancelEdit();
                          }}
                          disabled={updateModelName.isPending}
                          title="Cancel"
                          data-testid={`model-name-cancel-${model.id}`}
                          className="p-1 text-text-muted hover:text-text-bright hover:bg-panel-border/40 border border-panel-border rounded-[2px] cursor-pointer disabled:opacity-50 transition-colors"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      // 2026-05-27 — pencil is ALWAYS visible (opacity-60
                      // baseline, brightens to 100 on hover) so admins don't
                      // need to discover the affordance via hover. Hover-only
                      // pencils were the original ship and tested too
                      // hidden in practice.
                      <div className="flex items-center gap-1.5">
                        <span>{model.name}</span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            startEdit(model.id, model.name);
                          }}
                          title="Rename"
                          data-testid={`model-name-edit-${model.id}`}
                          className="opacity-60 hover:opacity-100 p-1 text-text-dim hover:text-text-bright bg-transparent border-none cursor-pointer rounded-[2px] hover:bg-panel-border/40 transition-all"
                        >
                          <Pencil size={12} />
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-2 text-sm font-mono text-text-muted tabular-nums">
                    {model.version}
                  </td>
                  {/* variables-counter-coherence Inc-6 (2026-05-20): show the
                    canonical addressable count (scalar + table cells) so
                    admin matches the builder's TRACKED total. Falls back to
                    the scalar-only `inputCount` for pre-Inc-1 registry rows
                    where `addressable_input_count IS NULL` until re-ingest. */}
                  <td
                    className="px-2 py-2 text-sm font-mono text-text-muted tabular-nums"
                    title={
                      model.addressableInputCount != null &&
                      model.addressableInputCount !== model.inputCount
                        ? `${model.inputCount} scalar + ${
                            model.addressableInputCount - model.inputCount
                          } table cells`
                        : undefined
                    }
                  >
                    {model.addressableInputCount ?? model.inputCount} inputs
                  </td>
                  <td className="px-2 py-2 text-sm font-mono text-text-muted tabular-nums">
                    {model.outputCount} outputs
                  </td>
                  <td className="px-2 py-2">
                    <select
                      aria-label={`Signal variant for ${model.name}`}
                      data-testid={`signal-variant-select-${model.id}`}
                      value={model.signal_variant ?? ''}
                      disabled={updateSignalVariant.isPending}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => {
                        e.stopPropagation();
                        const next = e.target.value as '' | SignalVariant;
                        updateSignalVariant.mutate({
                          id: model.id,
                          signal_variant: next === '' ? null : next,
                        });
                      }}
                      className="px-2 py-1 text-xs font-mono text-text-bright bg-input-bg border border-input-border rounded-[2px] cursor-pointer outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <option value="">(unassigned)</option>
                      <option value="operating-asset">Operating Asset</option>
                      <option value="new-development">New Development</option>
                    </select>
                  </td>
                  <td className="px-2 py-2">
                    {model.isActive ? (
                      <StatusBadge status="Active" />
                    ) : (
                      <span className="text-xs font-mono text-text-muted">
                        Disabled
                      </span>
                    )}
                  </td>
                  {/* builder-ux-overhaul Phase 5 Session 3 (2026-05-22) — enable/disable toggle.
                    stopPropagation prevents the row's onSelectModel from firing.
                    Disable is gated behind <ConfirmModal>; Enable fires immediately. */}
                  <td className="px-2 py-2">
                    {model.isActive ? (
                      <button
                        data-testid={`disable-model-btn-${model.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setPendingDisableId(model.id);
                        }}
                        disabled={updateModelEnabled.isPending}
                        className={ENABLE_BTN_CLASS.active}
                      >
                        Disable
                      </button>
                    ) : (
                      <button
                        data-testid={`enable-model-btn-${model.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          updateModelEnabled.mutate({
                            id: model.id,
                            isActive: true,
                          });
                        }}
                        disabled={updateModelEnabled.isPending}
                        className={ENABLE_BTN_CLASS.inactive}
                      >
                        Enable
                      </button>
                    )}
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex items-center gap-1">
                      {onConfigure && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onConfigure(model.id);
                          }}
                          className="px-2 py-1 text-xs font-mono text-accent bg-transparent border border-accent/15 cursor-pointer rounded-[2px] hover:bg-accent-dim transition-colors"
                        >
                          Configure
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (confirm('Delete this model?'))
                            deleteModel.mutate(model.id);
                        }}
                        className="p-1.5 text-text-dim hover:text-danger bg-transparent border-none cursor-pointer rounded-[2px] hover:bg-danger/[0.06] transition-colors"
                        title="Delete model"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* builder-ux-overhaul Phase 5 Session 3 (2026-05-22) — confirm before
          disabling a model family. Disabling drops the family from the
          new-deal list (listActiveModels) but leaves existing conversations
          unaffected (model-loader exemption + FK ON DELETE RESTRICT). */}
      <ConfirmModal
        open={pendingDisableId !== null}
        title="DISABLE MODEL"
        confirmLabel="Disable"
        danger
        onClose={() => setPendingDisableId(null)}
        onConfirm={() => {
          if (pendingDisableId) {
            updateModelEnabled.mutate(
              { id: pendingDisableId, isActive: false },
              { onSettled: () => setPendingDisableId(null) }
            );
          }
        }}
      >
        Disable this model? No new conversations can be created against it.
        Existing conversations are unaffected.
      </ConfirmModal>
    </Panel>
  );
}
